import { makeAutoObservable } from "mobx";
import * as maptilersdk from '@maptiler/sdk';
import { FeatureCollection } from 'geojson';
import { extremelySteepInclineColor, moderateInclineColor, noInclineColor, steepInclineColor, verySteepInclineColor } from "../utils/ColorUtils";
import { Permission } from "./UserService";
import { TemperatureLayer, PrecipitationLayer, WindLayer, PressureLayer, ColorRamp, RadarLayer } from "@maptiler/weather";
import { Air, Speed, Thermostat, Thunderstorm, WbCloudy } from "@mui/icons-material";

export interface IMapStyle {
    id: string;
    name: string;
    image: string;
}

export interface IMapWeatherLayer {
    id: string;
    name: string;
    icon: React.ElementType;
}


maptilersdk.config.apiKey = 'r0MEeCVGTMb6ZcYa0AFo';
export const styles: IMapStyle[] = [
    { id: "satellite", name: "Satellite", image: `${process.env.PUBLIC_URL}/images/satellite.png` },
    { id: "outdoor-v2", name: "Standard", image: `${process.env.PUBLIC_URL}/images/outdoor-v2.png` },
];

export const weatherLayers: IMapWeatherLayer[] = [
    { id: "temperature", name: "Temperature", icon: Thermostat },
    { id: "precipitation", name: "Precipitation", icon: Thunderstorm },
    { id: "wind", name: "Wind", icon: Air },
    { id: "pressure", name: "Pressure", icon: Speed },
    {
        id: 'radar',
        name: 'Radar',
        icon: WbCloudy
    }
];

export default class MapService {

    get map() {
        return this._map;
    }

    get mapStyle() {
        return this._mapStyle;
    }

    get currentWeatherMapLayers() {
        return this._currentWeatherMapLayers;
    }

    get loaded() {
        return this._loaded;
    }

    get weatherLayersLoaded(): Record<string, boolean> {
        return {
            temperature: this._temperatureLayerLoaded,
            precipitation: this._precipitationLayerLoaded,
            wind: this._windLayerLoaded,
            pressure: this._pressureLayerLoaded
        }
    }

    private _map: maptilersdk.Map | null = null;
    private _mapStyle: IMapStyle = styles[0];
    private _currentWeatherMapLayers: IMapWeatherLayer[] | null = null;
    private _loaded = false;
    private _bounds: [number, number, number, number] = [0, 0, 0, 0];

    private _temperatureLayer: TemperatureLayer | null = null;
    private _precipitationLayer: PrecipitationLayer | null = null;
    private _windLayer: WindLayer | null = null;
    private _pressureLayer: PressureLayer | null = null;
    private _radarLayer: RadarLayer | null = null;

    private _temperatureLayerLoaded = false;
    private _precipitationLayerLoaded = false;
    private _windLayerLoaded = false;
    private _pressureLayerLoaded = false;
    private _radarLayerLoaded = false;

    private _polyline: FeatureCollection = {
        'type': "FeatureCollection",
        'features': []
    };
    private _waypoints: [number, number][] = [];
    private _popups: { popup: maptilersdk.Popup, type: string }[] = [];

    constructor() {
        makeAutoObservable(this, undefined, { autoBind: true, deep: false })
    }

    init(bounds: [number, number, number, number], permission: Permission, showControls = true) {
        if (this._map) {
            this.dispose();
        }

        this._map = new maptilersdk.Map({
            container: 'map',
            style: this._mapStyle.id,
            bounds,
            zoom: 14,
            maptilerLogo: false,
            geolocateControl: false,
            terrain: permission !== Permission.free,
            navigationControl: showControls,
        });

        this._bounds = bounds;

        this._map.on('terrain', this.onTerrain);

        this._map.on('load', this.onLoad);

        this._map.on("styledata", this.onStyleData);
    }

    toggleTerrain() {
        if (this._map) {
            if (this._map.hasTerrain()) {
                this._map.disableTerrain();
            } else {
                this._map.enableTerrain();
            }
        }
    }

    onTerrain() {

        if (this._map) {
            if (this._map.hasTerrain()) {
                this.changePitch(this._map.getMaxPitch());
            } else {
                this.changePitch(0);
            }
        }
    }

    onLoad() {
        if (this._map) {
            this._map.fitBounds(this._bounds, { padding: 20 });
            setTimeout(() => {
                if (this._map)
                    this._map.setMaxBounds(this._map.getBounds());
                this._loaded = true;
            }, 3000);
        }
    }

    onStyleData(e: maptilersdk.MapDataEvent) {
        if (this._map && this._polyline && this._waypoints) {
            this.addPolylineLayer();
            this.addWaypointsLayer();
            this.addPointLayer();
        }
    }

    dispose() {
        if (this._map) {
            this._map.off('load', this.onLoad);
            this._map.off('terrain', this.onTerrain);
            this._map.off('styledata', this.onStyleData);
            this._map.remove();
            this._map = null;
            this._loaded = false;
            this._polyline = {
                'type': "FeatureCollection",
                'features': []
            };
            this._waypoints = [];
            this._temperatureLayer = null;
            this._precipitationLayer = null;
            this._windLayer = null;
            this._pressureLayer = null;
            this._radarLayer = null;
            this._currentWeatherMapLayers = null;
            this._mapStyle = styles[0];
            this._popups = [];
        }
    }

    setMapStyle(mapStyleId: string) {
        const mapStyle = styles.find(s => s.id === mapStyleId);
        if (!mapStyle || mapStyle === this._mapStyle) {
            return;
        }
        this._mapStyle = mapStyle;
        if (this._map) {
            this._loaded = false;
            this._map.setStyle(mapStyle.id);
        }
    }

    initWeatherLayers() {
        if (this._map && !this._temperatureLayer && !this._precipitationLayer && !this._windLayer && !this._pressureLayer && !this._radarLayer) {
            this._temperatureLayer = new TemperatureLayer({
                colorramp: ColorRamp.builtin.TEMPERATURE_3,
                id: 'temperature'
            });
            this._temperatureLayer.setOpacity(0);
            this._map.addLayer(this._temperatureLayer);
            this._temperatureLayer.animateByFactor(0);

            this._precipitationLayer = new PrecipitationLayer({
                id: 'precipitation'
            });
            this._precipitationLayer.setOpacity(0);
            this._map.addLayer(this._precipitationLayer);
            this._precipitationLayer.animateByFactor(0);

            this._windLayer = new WindLayer({
                colorramp: ColorRamp.builtin.NULL,
                color: [255, 255, 255, 100],
                fastColor: [255, 255, 255, 100],
                id: 'wind',
            });
            this._map.addLayer(this._windLayer);
            this._windLayer.animateByFactor(0);

            this._pressureLayer = new PressureLayer({
                id: 'pressure'
            });
            this._pressureLayer.setOpacity(0);
            this._map.addLayer(this._pressureLayer);
            this._pressureLayer.animateByFactor(0);

            this._radarLayer = new RadarLayer({
                id: 'radar',
            });
            this._radarLayer.setOpacity(0);
            this._map.addLayer(this._radarLayer);
            this._radarLayer.animateByFactor(0);


            this._temperatureLayer?.onSourceReadyAsync().then(() => {
                this._temperatureLayer?.setOpacity(0);
                this._temperatureLayerLoaded = true;
            });
            this._precipitationLayer?.onSourceReadyAsync().then(() => {
                this._precipitationLayer?.setOpacity(0);
                this._precipitationLayerLoaded = true;
            });
            this._windLayer?.onSourceReadyAsync().then(() => {
                this._windLayer?.setOpacity(0);
                this._windLayerLoaded = true;
            });
            this._pressureLayer?.onSourceReadyAsync().then(() => {
                this._pressureLayer?.setOpacity(0);
                this._pressureLayerLoaded = true;
            });
            this._radarLayer?.onSourceReadyAsync().then(() => {
                this._radarLayer?.setOpacity(0);
                this._radarLayerLoaded = true;
            });
            this._currentWeatherMapLayers = null;
        }
    }

    setWeatherLayer(weatherLayer: IMapWeatherLayer | null) {
        if (weatherLayer) {
            if (this._currentWeatherMapLayers) {
                if (weatherLayer.id === "wind") {
                    this._currentWeatherMapLayers = [...this._currentWeatherMapLayers, weatherLayer];
                } else {
                    this._currentWeatherMapLayers = this._currentWeatherMapLayers.filter(layer => layer.id === "wind");
                    this._currentWeatherMapLayers.push(weatherLayer);
                }
            } else {
                this._currentWeatherMapLayers = [weatherLayer];
            }
        } else {
            this._currentWeatherMapLayers = null;
        }

        switch (weatherLayer?.id) {
            case "temperature":
                if (this._map) {
                    this._temperatureLayer?.setOpacity(0.5);
                    this._precipitationLayer?.setOpacity(0);
                    this._pressureLayer?.setOpacity(0);
                    this._radarLayer?.setOpacity(0);
                }
                break;
            case "precipitation":
                if (this._map) {
                    this._temperatureLayer?.setOpacity(0);
                    this._precipitationLayer?.setOpacity(0.5);
                    this._pressureLayer?.setOpacity(0);
                    this._radarLayer?.setOpacity(0);
                }
                break;
            case "wind":
                if (this._map) {
                    this._windLayer?.setOpacity(1);
                }
                break;
            case "pressure":
                if (this._map) {
                    this._temperatureLayer?.setOpacity(0);
                    this._precipitationLayer?.setOpacity(0);
                    this._pressureLayer?.setOpacity(0.5);
                    this._radarLayer?.setOpacity(0);
                }
                break;
            case "radar":
                if (this._map) {
                    this._temperatureLayer?.setOpacity(0);
                    this._precipitationLayer?.setOpacity(0);
                    this._pressureLayer?.setOpacity(0);
                    this._radarLayer?.setOpacity(0.5);
                }
                break;
            default:
                if (this._map) {
                    this._temperatureLayer?.setOpacity(0);
                    this._precipitationLayer?.setOpacity(0);
                    this._windLayer?.setOpacity(0);
                    this._pressureLayer?.setOpacity(0);
                    this._radarLayer?.setOpacity(0);
                }
        }
    }

    unsetWeatherLayer(weatherLayer: IMapWeatherLayer) {
        this._currentWeatherMapLayers = this._currentWeatherMapLayers?.filter(layer => layer.id !== weatherLayer.id) || null;
        if (this._currentWeatherMapLayers?.length === 0) {
            this._currentWeatherMapLayers = null;
        }

        switch (weatherLayer?.id) {
            case "temperature":
                if (this._map) {
                    this._temperatureLayer?.setOpacity(0);
                }
                break;
            case "precipitation":
                if (this._map) {
                    this._precipitationLayer?.setOpacity(0);
                }
                break;
            case "wind":
                if (this._map) {
                    this._windLayer?.setOpacity(0);
                }
                break;
            case "pressure":
                if (this._map) {
                    this._pressureLayer?.setOpacity(0);
                }
                break;
            case "radar":
                if (this._map) {
                    this._radarLayer?.setOpacity(0);
                }
                break;
        }
    }

    setWeatherTime(seconds: number) {
        if (this._temperatureLayer) {
            this._temperatureLayer.setAnimationTime(seconds);
        }
        if (this._precipitationLayer) {
            this._precipitationLayer.setAnimationTime(seconds);
        }
        if (this._windLayer) {
            this._windLayer.setAnimationTime(seconds);
        }
        if (this._pressureLayer) {
            this._pressureLayer.setAnimationTime(seconds);
        }
        if (this._radarLayer) {
            this._radarLayer.setAnimationTime(seconds);
        }
    }

    getWeatherData(latlng: [number, number]) {
        return {
            date: new Date((this._temperatureLayer?.getAnimationTime() || 0) * 1000 || 0),
            temperature: this._temperatureLayer?.pickAt(latlng[0], latlng[1]),
            precipitation: this._precipitationLayer?.pickAt(latlng[0], latlng[1]),
            wind: this._windLayer?.pickAt(latlng[0], latlng[1]),
            pressure: this._pressureLayer?.pickAt(latlng[0], latlng[1]),
            radar: this._radarLayer?.pickAt(latlng[0], latlng[1])
        }
    }


    addPolyline(geojson: FeatureCollection) {
        this._polyline = geojson;
        const source = this._map?.getSource('line');
        if (this._map && source) {
            (source as maptilersdk.GeoJSONSource).setData(geojson);
        }
    }

    addPolylineLayer() {

        if (this._map && !this._map?.getSource('line')) {
            this._map.addSource('line', {
                type: 'geojson',
                lineMetrics: true,
                data: this._polyline
            });

            this._map.addLayer({
                type: 'line',
                source: 'line',
                id: 'line',
                paint: {
                    'line-width': 4,
                    'line-color': [
                        'interpolate',
                        ['linear'],
                        ['get', 'slope'],
                        1,
                        noInclineColor,
                        2,
                        moderateInclineColor,
                        3,
                        steepInclineColor,
                        4,
                        verySteepInclineColor,
                        5,
                        extremelySteepInclineColor
                    ]
                },
                layout: {
                    'line-cap': 'butt',
                    'line-join': 'bevel'
                }
            });
        }
    }

    setPointPostion(lat: number, lng: number) {
        if (this._map) {
            const source = this._map.getSource('point');
            if (source) {
                (source as maptilersdk.GeoJSONSource).setData({
                    'type': 'Point',
                    'coordinates': [lng, lat]
                });
            }
        }
    }

    addPointLayer() {
        if (this._map && !this._map?.getSource('point')) {
            this._map.addSource('point', {
                'type': 'geojson',
                'data': {
                    'type': 'Point',
                    'coordinates': [0, 0]
                }
            });

            this._map.addLayer({
                'id': 'point',
                'source': 'point',
                'type': 'circle',
                'paint': {
                    'circle-radius': 4,
                    'circle-color': '#000000'
                }
            });
        }
    }

    addWaypoints(points: [number, number][]) {
        this._waypoints = points;

        const source = this._map?.getSource('waypoints');
        if (this._map && source) {
            (source as maptilersdk.GeoJSONSource).setData({
                'type': "FeatureCollection",
                'features': points.map((point, index) => {
                    return {
                        'type': 'Feature',
                        'geometry': {
                            'type': 'Point',
                            'coordinates': point
                        },
                        'properties': {
                            'type': index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'waypoint',
                        }
                    };
                })
            });
        }
    }

    addWaypointsLayer() {
        if (this._map && !this._map?.getSource('waypoints')) {
            this._map.addSource('waypoints', {
                'type': 'geojson',
                'data': {
                    'type': "FeatureCollection",
                    'features': this._waypoints.map((point, index) => {
                        return {
                            'type': 'Feature',
                            'geometry': {
                                'type': 'Point',
                                'coordinates': point
                            },
                            'properties': {
                                'type': index === 0 ? 'start' : index === this._waypoints.length - 1 ? 'end' : 'waypoint',
                            }
                        };
                    })
                }
            });

            this._map.addLayer({
                'id': 'waypoints',
                'source': 'waypoints',
                'type': 'circle',
                'paint': {
                    'circle-radius': 4,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': [
                        "match",
                        ["get", "type"],
                        "start", noInclineColor,
                        "#000000"
                    ],
                    'circle-color': [
                        "match",
                        ["get", "type"],
                        "start", noInclineColor,
                        "end", "#000000",
                        "transparent"
                    ]
                }
            });
        }
    }

    addPopUp(latlng: [number, number], content: string, type: string) {
        if (this._map) {
            for (const popupObj of this._popups) {
                if (popupObj.type === type) {
                    popupObj.popup.remove();
                }
            }
            const popup = new maptilersdk.Popup({
                closeButton: false,
                closeOnClick: false,
                className: type
            })
                .setLngLat(latlng)
                .setHTML(content)
                .addTo(this._map);
            this._popups.push({ popup, type });
        }
    }

    removeAllPopups() {
        if (this._map) {
            for (const popupObj of this._popups) {
                popupObj.popup.remove();
            }
            this._popups = [];
        }
    }

    private changePitch(pitch: number, duration = 1000) {
        if (!this._map) {
            return;
        }

        const easeOptions = {
            pitch: pitch,
            duration: duration,
            easing: (x: number) => (x < 0.5 ? 16 * x ** 5 : 1 - (-2 * x + 2) ** 5 / 2)
        };
        this._map.easeTo(easeOptions);
    }
}